import { useMemo, useState } from "react";
import {
  adminCreateB2bGrant,
  adminRevokeB2bGrant,
  createB2bPartnerAdmin,
  useAction,
} from "wasp/client/operations";
import { Button } from "../../../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../client/components/ui/dialog";
import { Input } from "../../../client/components/ui/input";
import { Label } from "../../../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../client/components/ui/select";
import { useToast } from "../../../client/hooks/use-toast";
import useTranslation from "../../../client/hooks/useTranslation";

export interface PartnerOption {
  id: string;
  name: string;
}

/** Affiche UNE fois la clé API en clair (création ou rotation). Elle n'est
 *  jamais stockée côté serveur : fermer cette modale la perd définitivement. */
export function KeyRevealDialog({
  apiKey,
  partnerName,
  open,
  onClose,
}: {
  apiKey: string | null;
  partnerName: string;
  open: boolean;
  onClose: () => void;
}) {
  const t = useTranslation();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey ?? "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* presse-papier indisponible : la clé reste sélectionnable à la main */
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t.adminB2b.keyDialogTitle} {partnerName}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t.adminB2b.keyDialogWarning}</p>
        <code className="block select-all break-all rounded-md border border-border bg-accent/40 p-3 font-mono text-sm">
          {apiKey}
        </code>
        <DialogFooter>
          <Button variant="outline" onClick={handleCopy}>
            {copied ? t.adminB2b.copied : t.adminB2b.copy}
          </Button>
          <Button onClick={onClose}>{t.adminB2b.close}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewPartnerDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (result: { name: string; apiKey: string }) => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const create = useAction(createB2bPartnerAdmin);
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = async () => {
    setIsSubmitting(true);
    try {
      const trimmedEmail = contactEmail.trim();
      const result = await create({
        name: name.trim(),
        // Le schéma Zod refuse "" (.email().optional()) : absent = undefined.
        contactEmail: trimmedEmail === "" ? undefined : trimmedEmail,
      });
      setName("");
      setContactEmail("");
      onOpenChange(false);
      onCreated({ name: result.name, apiKey: result.apiKey });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.adminB2b.newPartner}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="b2b-partner-name">{t.adminB2b.partnerName} *</Label>
            <Input
              id="b2b-partner-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="b2b-partner-email">{t.adminB2b.contactEmail}</Label>
            <Input
              id="b2b-partner-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.adminB2b.cancel}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isSubmitting || name.trim().length < 2}
          >
            {t.adminB2b.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewGrantDialog({
  open,
  onOpenChange,
  partners,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partners: PartnerOption[];
  onCreated: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const create = useAction(adminCreateB2bGrant);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [email, setEmail] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Un seul partenaire : pré-sélectionné d'office.
  const partnerId =
    selectedPartnerId || (partners.length === 1 ? partners[0].id : "");

  const handleCreate = async () => {
    if (!partnerId) return;
    setIsSubmitting(true);
    try {
      const ref = externalRef.trim();
      await create({
        partnerId,
        email: email.trim(),
        externalRef: ref === "" ? undefined : ref,
      });
      toast({ title: t.adminB2b.createdToast });
      setEmail("");
      setExternalRef("");
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.adminB2b.newGrantDialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>{t.adminB2b.colPartner} *</Label>
            <Select value={partnerId} onValueChange={setSelectedPartnerId}>
              <SelectTrigger>
                <SelectValue placeholder={t.adminB2b.colPartner} />
              </SelectTrigger>
              <SelectContent>
                {partners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="b2b-grant-email">{t.adminB2b.email} *</Label>
            <Input
              id="b2b-grant-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="b2b-grant-ref">{t.adminB2b.externalRef}</Label>
            <Input
              id="b2b-grant-ref"
              className="font-mono"
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.adminB2b.cancel}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isSubmitting || !partnerId || !email.trim()}
          >
            {t.adminB2b.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Date -> "YYYY-MM-DDTHH:mm" en heure locale (format d'un input datetime-local). */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function RevokeGrantDialog({
  grant,
  onClose,
  onRevoked,
}: {
  grant: { id: string; email: string } | null;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const revoke = useAction(adminRevokeB2bGrant);
  const [endsAtLocal, setEndsAtLocal] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Borne basse du sélecteur, calculée à l'ouverture du dialogue (grant change).
  const minLocal = useMemo(
    () => (grant ? toDatetimeLocal(new Date()) : ""),
    [grant],
  );

  const close = () => {
    setEndsAtLocal("");
    onClose();
  };

  const submit = async (endsAt?: string) => {
    if (!grant) return;
    setIsSubmitting(true);
    try {
      const result = await revoke({ grantId: grant.id, endsAt });
      toast({
        title:
          result.status === "scheduled"
            ? t.adminB2b.revokeScheduledToast
            : t.adminB2b.revokedToast,
      });
      close();
      onRevoked();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduled = () => {
    const date = new Date(endsAtLocal);
    if (Number.isNaN(date.getTime())) return;
    // Une date passée serait une coupure IMMÉDIATE et irréversible côté
    // serveur ; on la bloque côté client.
    if (date.getTime() <= Date.now()) {
      toast({ title: t.adminB2b.revokePastDate, variant: "destructive" });
      return;
    }
    // datetime-local = heure locale du navigateur ; le serveur attend un ISO.
    void submit(date.toISOString());
  };

  return (
    <Dialog open={!!grant} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.adminB2b.revokeDialogTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm font-medium break-all">{grant?.email}</p>
        <div className="space-y-3">
          <Button
            variant="destructive"
            className="w-full"
            disabled={isSubmitting}
            onClick={() => void submit(undefined)}
          >
            {t.adminB2b.revokeNow}
          </Button>
          <div className="space-y-1">
            <Label htmlFor="b2b-revoke-at">{t.adminB2b.colEndsAt}</Label>
            <div className="flex gap-2">
              <Input
                id="b2b-revoke-at"
                type="datetime-local"
                min={minLocal}
                value={endsAtLocal}
                onChange={(e) => setEndsAtLocal(e.target.value)}
              />
              <Button
                variant="outline"
                disabled={isSubmitting || !endsAtLocal}
                onClick={handleScheduled}
              >
                {t.adminB2b.revokeAtDate}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t.adminB2b.cancel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
